const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage = window[this.type];
      const testKey = "__storage_test__";
      storage.setItem(testKey, "test");
      storage.removeItem(testKey);
      this.storage = storage;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage, prefix) {
    this.storage = storage;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
new StorageHelper("sessionStorage");
const CONSENT_STORAGE_KEY = "cookieSelections";
const CONSENT_DENIED_DEFAULTS = { personalization: false, marketing: false, analytics: false };
const CONSENT_KEYS = ["personalization", "marketing", "analytics"];
class CookieBanner extends HTMLElement {
  consentApiReady = false;
  /** A choice made before the Shopify API loaded; sent to Shopify once it is ready. */
  pendingShopifyConsent = null;
  async connectedCallback() {
    this.setupConsent();
    this.addEventListeners();
    this.activateTab("consent");
    this.syncCheckboxGroups();
    await this.loadShopifyConsentApi();
    if (this.dataset.forceShow === "true" || await this.shouldShowBanner()) {
      this.showBanner();
    }
  }
  /** Sets gtag consent defaults and fires `edition:consent-default-set`. */
  setupConsent = () => {
    this.ensureGtag();
    const selections = local.get(CONSENT_STORAGE_KEY) ?? CONSENT_DENIED_DEFAULTS;
    window.gtag?.("consent", "default", {
      functionality_storage: "granted",
      analytics_storage: selections.analytics ? "granted" : "denied",
      personalization_storage: selections.personalization ? "granted" : "denied",
      ad_personalization: selections.personalization ? "granted" : "denied",
      ad_storage: selections.marketing ? "granted" : "denied",
      ad_user_data: selections.marketing ? "granted" : "denied",
      wait_for_update: 3e3
    });
    window.gtag?.("set", "ads_data_redaction", !selections.marketing);
    window.gtag?.("set", "url_passthrough", true);
    window.dispatchEvent(new CustomEvent("edition:consent-default-set"));
  };
  addEventListeners = () => {
    this.querySelector("#tab-consent")?.addEventListener("click", () => this.activateTab("consent"));
    this.querySelector("#tab-details")?.addEventListener("click", () => this.activateTab("details"));
    this.querySelector(".allow-all-btn")?.addEventListener("click", this.handleAllowAll);
    this.querySelector(".allow-selected-btn")?.addEventListener("click", this.handleAllowSelected);
    this.querySelector('[data-action="reject"]')?.addEventListener("click", this.handleRejectAll);
    document.getElementById("manage-cookie-consent")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.showBanner();
    });
  };
  activateTab = (tab) => {
    ["consent", "details"].forEach((id) => {
      this.querySelector(`#tab-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("tw:hidden!", id !== tab);
    });
  };
  /**
   * Returns true if the banner should be shown.
   * Prefers localStorage over the Shopify API — if the user has a stored selection we trust it.
   */
  shouldShowBanner = async () => {
    if (local.get(CONSENT_STORAGE_KEY)) {
      return false;
    }
    if (!this.consentApiReady || !window.Shopify?.customerPrivacy?.currentVisitorConsent) {
      return true;
    }
    try {
      const { preferences, analytics, marketing } = await window.Shopify.customerPrivacy.currentVisitorConsent();
      return [preferences, analytics, marketing].every((v) => (v ?? "") === "");
    } catch {
      return true;
    }
  };
  loadShopifyConsentApi = () => {
    if (!window.Shopify?.loadFeatures) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
          if (error) {
            logger.error("Shopify consent-tracking-api failed to load", error);
            resolve();
            return;
          }
          this.consentApiReady = true;
          this.flushPendingShopifyConsent();
          resolve();
          document.addEventListener("visitorConsentCollected", async () => {
            try {
              const v = await window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
              if (!v) {
                return;
              }
              this.setConsentGroup({ key: "personalization", checked: v.preferences === "yes" });
              this.setConsentGroup({ key: "marketing", checked: v.marketing === "yes" });
              this.setConsentGroup({ key: "analytics", checked: v.analytics === "yes" });
              this.saveSelections();
            } catch (err) {
              logger.error("visitorConsentCollected handler error", err);
            }
          });
        });
      } catch (e) {
        logger.error("Shopify loadFeatures threw", e);
        resolve();
      }
    });
  };
  /** Updates gtag consent, persists to localStorage, and fires `edition:consent-updated`. */
  applyConsent = ({ consent, hide }) => {
    window.gtag?.("consent", "update", {
      functionality_storage: "granted",
      analytics_storage: consent.analytics === "yes" ? "granted" : "denied",
      personalization_storage: consent.preferences === "yes" ? "granted" : "denied",
      ad_personalization: consent.preferences === "yes" ? "granted" : "denied",
      ad_storage: consent.marketing === "yes" ? "granted" : "denied",
      ad_user_data: consent.marketing === "yes" ? "granted" : "denied"
    });
    window.gtag?.("set", "ads_data_redaction", consent.marketing !== "yes");
    this.saveSelections();
    window.dispatchEvent(new CustomEvent("edition:consent-updated"));
    this.pendingShopifyConsent = consent;
    this.flushPendingShopifyConsent();
    if (hide) {
      this.hideBanner();
    }
  };
  /** The banner can be answered before the Shopify API has loaded; send the choice as soon as it can be. */
  flushPendingShopifyConsent = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (!this.pendingShopifyConsent || !this.consentApiReady || !customerPrivacy?.setTrackingConsent) {
      return;
    }
    const { analytics, marketing, preferences } = this.pendingShopifyConsent;
    this.pendingShopifyConsent = null;
    customerPrivacy.setTrackingConsent({ analytics: analytics === "yes", marketing: marketing === "yes", preferences: preferences === "yes" });
  };
  handleAllowAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = true;
      }
    });
    this.applyConsent({ consent: { preferences: "yes", marketing: "yes", analytics: "yes" }, hide: true });
  };
  handleAllowSelected = () => {
    const { personalization, marketing, analytics } = this.getSelections();
    this.applyConsent({
      consent: {
        preferences: personalization ? "yes" : "no",
        marketing: marketing ? "yes" : "no",
        analytics: analytics ? "yes" : "no"
      },
      hide: true
    });
  };
  handleRejectAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = false;
      }
    });
    this.applyConsent({ consent: { preferences: "no", marketing: "no", analytics: "no" }, hide: true });
  };
  /** Keeps the consent/details tab panels in sync — the same consent key can appear in both tabs. */
  syncCheckboxGroups = () => {
    const checkboxes = Array.from(this.querySelectorAll("input.ed-checkbox[data-consent]"));
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", ({ currentTarget }) => {
        const source = currentTarget;
        checkboxes.forEach((other) => {
          if (other !== source && other.dataset.consent === source.dataset.consent && !other.disabled) {
            other.checked = source.checked;
          }
        });
      });
    });
    const grouped = checkboxes.reduce((acc, cb) => {
      const key = cb.dataset.consent;
      (acc[key] ??= []).push(cb);
      return acc;
    }, {});
    Object.values(grouped).forEach(([source, ...rest]) => {
      rest.forEach((cb) => {
        if (!cb.disabled) {
          cb.checked = source.checked;
        }
      });
    });
  };
  setConsentGroup = ({ key, checked }) => {
    this.querySelectorAll(`input.ed-checkbox[data-consent="${key}"]`).forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = checked;
      }
    });
  };
  saveSelections = () => {
    local.set(CONSENT_STORAGE_KEY, this.getSelections());
  };
  getSelections = () => CONSENT_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: !!this.querySelector(`input.ed-checkbox[data-consent="${key}"]`)?.checked
    }),
    { personalization: false, marketing: false, analytics: false }
  );
  get overlay() {
    return this.querySelector("[data-cookie-overlay]") ?? this;
  }
  /**
   * Opens the banner. Applies any previously saved selections to the checkboxes first —
   * if nothing is stored, the HTML defaults remain as-is.
   * Can be triggered externally via `#manage-cookie-consent`.
   */
  showBanner = () => {
    const saved = local.get(CONSENT_STORAGE_KEY);
    if (saved) {
      CONSENT_KEYS.forEach((key) => this.setConsentGroup({ key, checked: saved[key] }));
    }
    this.overlay.removeAttribute("hidden");
    this.overlay.classList.remove("tw:hidden!");
  };
  hideBanner = () => {
    this.overlay.classList.add("tw:hidden!");
  };
  /** Creates a `window.gtag` shim that queues calls into `dataLayer` if the real gtag hasn't loaded yet. */
  ensureGtag = () => {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };
}
const DEFAULT_LIMIT_MOBILE = 5;
const DEFAULT_LIMIT_TABLET = 6;
const DEFAULT_LIMIT_LAPTOP = 5;
const DEFAULT_LIMIT_DESKTOP = 6;
const MOBILE_QUERY$1 = "(max-width: 740px)";
const TABLET_QUERY = "(min-width: 741px) and (max-width: 999px)";
const LAPTOP_QUERY = "(min-width: 1000px) and (max-width: 1399px)";
class VariantSiblings extends HTMLElement {
  links = [];
  moreButton = null;
  isExpanded = false;
  mobileMediaQuery = window.matchMedia(MOBILE_QUERY$1);
  tabletMediaQuery = window.matchMedia(TABLET_QUERY);
  laptopMediaQuery = window.matchMedia(LAPTOP_QUERY);
  constructor() {
    super();
    this.handleExpand = this.handleExpand.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
  }
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll(".product-form__custom-liquid > a")).filter((link) => link.querySelector("img") !== null);
    this.mobileMediaQuery.addEventListener("change", this.handleBreakpointChange);
    this.tabletMediaQuery.addEventListener("change", this.handleBreakpointChange);
    this.laptopMediaQuery.addEventListener("change", this.handleBreakpointChange);
    this.collapse();
  }
  disconnectedCallback() {
    this.mobileMediaQuery.removeEventListener("change", this.handleBreakpointChange);
    this.tabletMediaQuery.removeEventListener("change", this.handleBreakpointChange);
    this.laptopMediaQuery.removeEventListener("change", this.handleBreakpointChange);
    this.moreButton?.removeEventListener("click", this.handleExpand);
  }
  /**
   * Limit declared in CSS as `--variant-siblings-limit`, for containers whose
   * width has nothing to do with the viewport — the quick buy panels are a
   * fixed ~400px at every desktop size, so the breakpoint tiers below would
   * always pick a row that does not fit.
   */
  getDeclaredLimit() {
    const declared = Number(getComputedStyle(this).getPropertyValue("--variant-siblings-limit"));
    if (Number.isInteger(declared) && declared > 1) {
      return declared;
    }
    return null;
  }
  /** Resolves the row limit for the current breakpoint. */
  getLimit() {
    const declared = this.getDeclaredLimit();
    if (declared !== null) {
      return declared;
    }
    let attribute;
    let fallback;
    if (this.mobileMediaQuery.matches) {
      attribute = this.getAttribute("limit-mobile");
      fallback = DEFAULT_LIMIT_MOBILE;
    } else if (this.tabletMediaQuery.matches) {
      attribute = this.getAttribute("limit-tablet");
      fallback = DEFAULT_LIMIT_TABLET;
    } else if (this.laptopMediaQuery.matches) {
      attribute = this.getAttribute("limit-laptop");
      fallback = DEFAULT_LIMIT_LAPTOP;
    } else {
      attribute = this.getAttribute("limit");
      fallback = DEFAULT_LIMIT_DESKTOP;
    }
    const parsed = Number(attribute);
    if (Number.isInteger(parsed) && parsed > 1) {
      return parsed;
    }
    return fallback;
  }
  /** Shows `limit - 1` links, hides the rest and puts the "+" tile in the last row slot. */
  collapse() {
    const limit = this.getLimit();
    this.style.setProperty("--variant-siblings-columns", String(limit));
    this.reset();
    if (this.isExpanded || this.links.length <= limit) {
      return;
    }
    const visibleCount = limit - 1;
    this.links.slice(visibleCount).forEach((link) => {
      link.setAttribute("hidden", "");
    });
    const button = document.createElement("button");
    button.type = "button";
    button.className = "variant-siblings__more";
    button.textContent = "+";
    button.setAttribute("aria-label", this.getAttribute("more-label") ?? "Vis flere varianter");
    button.addEventListener("click", this.handleExpand);
    this.links[visibleCount].insertAdjacentElement("beforebegin", button);
    this.moreButton = button;
  }
  /** Restores all links and removes the "+" tile. */
  reset() {
    this.links.forEach((link) => {
      link.removeAttribute("hidden");
    });
    if (this.moreButton !== null) {
      this.moreButton.removeEventListener("click", this.handleExpand);
      this.moreButton.remove();
      this.moreButton = null;
    }
  }
  handleExpand() {
    this.isExpanded = true;
    this.reset();
  }
  handleBreakpointChange() {
    this.collapse();
  }
}
class JudgeMeReviews extends HTMLElement {
  connectedCallback() {
    const widget = document.querySelector(".jdgm-widget.jdgm-review-widget");
    if (widget === null || this.contains(widget)) {
      if (widget === null) {
        this.removeTab();
      }
      return;
    }
    const sourceSection = widget.closest(".shopify-section");
    this.appendChild(widget);
    if (sourceSection !== null) {
      sourceSection.style.display = "none";
    }
  }
  /**
   * Removes the tab trigger and panel when there is no JudgeMe widget on the page.
   *
   * When the reviews tab is configured first it is also the open one, so
   * dropping it would leave every panel hidden — the first remaining tab is
   * opened in that case.
   */
  removeTab() {
    const trigger = document.querySelector("[data-judgeme-tab-trigger]");
    const panel = this.closest("[data-judgeme-tab-panel]");
    const wasOpen = panel !== null && !panel.hasAttribute("hidden");
    const tabList = panel?.parentElement ?? null;
    trigger?.remove();
    panel?.remove();
    if (wasOpen && tabList !== null) {
      this.openFirstTab(tabList);
    }
  }
  /** Opens the first remaining tab panel and marks its nav trigger as expanded. */
  openFirstTab(tabList) {
    const firstPanel = tabList.querySelector(".product-tabs__tab-item-wrapper");
    if (firstPanel === null) {
      return;
    }
    firstPanel.removeAttribute("hidden");
    firstPanel.querySelector("collapsible-content")?.setAttribute("open", "");
    firstPanel.querySelector(".collapsible-toggle")?.setAttribute("aria-expanded", "true");
    document.querySelector(`.tabs-nav__item[aria-controls="${firstPanel.id}"]`)?.setAttribute("aria-expanded", "true");
  }
}
const MOBILE_QUERY = "(max-width: 740px)";
const DEFAULT_CHARACTERS = 300;
class ProductDescription extends HTMLElement {
  content = null;
  toggle = null;
  isExpanded = false;
  /** True while the open/close height transition runs. */
  isAnimating = false;
  mobileMediaQuery = window.matchMedia(MOBILE_QUERY);
  resizeObserver = null;
  /** variant id → description HTML, from the JSON script rendered by Liquid. */
  variantDescriptions = {};
  /** The product description, used for variants without their own. */
  fallbackDescription = "";
  constructor() {
    super();
    this.handleToggle = this.handleToggle.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
  }
  connectedCallback() {
    this.content = this.querySelector("[data-product-description-content]");
    this.toggle = this.querySelector("[data-product-description-toggle]");
    if (this.content === null) {
      return;
    }
    this.readVariantDescriptions();
    this.readFallbackDescription();
    this.toggle?.addEventListener("click", this.handleToggle);
    this.content.addEventListener("transitionend", this.handleTransitionEnd);
    this.mobileMediaQuery.addEventListener("change", this.handleBreakpointChange);
    document.addEventListener("variant:change", this.handleVariantChange);
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isAnimating) {
        return;
      }
      this.collapse();
    });
    this.resizeObserver.observe(this.content);
    this.collapse();
  }
  disconnectedCallback() {
    this.toggle?.removeEventListener("click", this.handleToggle);
    this.content?.removeEventListener("transitionend", this.handleTransitionEnd);
    this.mobileMediaQuery.removeEventListener("change", this.handleBreakpointChange);
    document.removeEventListener("variant:change", this.handleVariantChange);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
  /** The character limit from the attribute, falling back to the default. */
  get characterLimit() {
    const parsed = Number(this.getAttribute("characters"));
    return Number.isInteger(parsed) && parsed > 0 ? parsed : DEFAULT_CHARACTERS;
  }
  /** Parses the variant id → description map Liquid rendered into the JSON script. */
  readVariantDescriptions() {
    const script = this.querySelector("[data-product-description-variants]");
    if (script === null) {
      return;
    }
    try {
      const parsed = JSON.parse(script.textContent ?? "{}");
      if (typeof parsed === "object" && parsed !== null) {
        this.variantDescriptions = parsed;
      }
    } catch {
      this.variantDescriptions = {};
    }
  }
  /** Reads the product description kept for variants without their own text. */
  readFallbackDescription() {
    const template = this.querySelector("[data-product-description-fallback]");
    this.fallbackDescription = template?.innerHTML.trim() ?? "";
  }
  /** Swaps in the selected variant's description, or the product description. */
  handleVariantChange(event) {
    const detail = event.detail;
    const variantId = detail?.variant?.id;
    if (this.content === null || variantId === void 0) {
      return;
    }
    const description = this.variantDescriptions[String(variantId)]?.trim();
    const html = description !== void 0 && description !== "" ? description : this.fallbackDescription;
    if (html === "" || this.content.innerHTML.trim() === html) {
      return;
    }
    this.content.innerHTML = html;
    this.isExpanded = false;
    this.setLabel();
    this.collapse();
  }
  /**
   * Clamps the description to the height of its first `characterLimit`
   * characters. Does nothing on desktop, while expanded, or when the
   * description is shorter than the limit.
   */
  collapse() {
    if (this.content === null || this.toggle === null) {
      return;
    }
    if (this.isExpanded || !this.mobileMediaQuery.matches) {
      this.reset();
      return;
    }
    const height = this.measureHeightAtLimit();
    if (height === null) {
      this.reset();
      return;
    }
    if (height <= 0) {
      return;
    }
    const value = `${Math.ceil(height)}px`;
    if (this.style.getPropertyValue("--product-description-height") === value && this.hasAttribute("collapsed")) {
      return;
    }
    this.style.setProperty("--product-description-height", value);
    this.setAttribute("collapsed", "");
    this.toggle.hidden = false;
    this.toggle.setAttribute("aria-expanded", "false");
  }
  /**
   * Pins the description's current rendered height as a real length.
   *
   * max-height animates between lengths only — an open description sits at
   * `none`, so closing it would jump without this. Opening needs the same
   * treatment in reverse: the target height has to be a length too.
   */
  pinCurrentHeight() {
    if (this.content === null) {
      return;
    }
    this.style.setProperty("--product-description-full", `${this.content.scrollHeight}px`);
    this.isAnimating = true;
    void this.content.offsetHeight;
  }
  /** Clears the collapsed state so the description renders at its natural height. */
  reset() {
    this.removeAttribute("collapsed");
    this.style.removeProperty("--product-description-height");
    if (this.toggle !== null) {
      this.toggle.hidden = !this.isExpanded;
      this.toggle.setAttribute("aria-expanded", String(this.isExpanded));
    }
  }
  /**
   * Distance from the top of the description to the end of the Nth character.
   *
   * @returns The height in pixels, or null when the description holds fewer
   *          characters than the limit (nothing to collapse).
   */
  measureHeightAtLimit() {
    if (this.content === null) {
      return null;
    }
    const limit = this.characterLimit;
    const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
    let seen = 0;
    let node = walker.nextNode();
    while (node !== null) {
      const length = node.data.length;
      if (seen + length >= limit) {
        const range = document.createRange();
        range.setStart(node, 0);
        range.setEnd(node, limit - seen);
        const rangeBottom = range.getBoundingClientRect().bottom;
        const contentTop = this.content.getBoundingClientRect().top;
        return rangeBottom - contentTop;
      }
      seen += length;
      node = walker.nextNode();
    }
    return null;
  }
  /**
   * Puts the right wording on the toggle for the current state. The button
   * shows only a chevron, so the wording lives in aria-label.
   */
  setLabel() {
    if (this.toggle === null) {
      return;
    }
    const label = this.isExpanded ? this.getAttribute("less-label") : this.getAttribute("more-label");
    if (label !== null) {
      this.toggle.setAttribute("aria-label", label);
    }
  }
  handleToggle() {
    this.isExpanded = !this.isExpanded;
    this.pinCurrentHeight();
    if (this.isExpanded) {
      this.reset();
    } else {
      this.collapse();
      this.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    this.setLabel();
  }
  /** Ends the animation and drops the pinned height once max-height settles. */
  handleTransitionEnd(event) {
    if (event.propertyName !== "max-height") {
      return;
    }
    this.isAnimating = false;
    this.style.removeProperty("--product-description-full");
  }
  handleBreakpointChange() {
    this.collapse();
  }
}
const PREFETCH_VIEWPORTS = 2;
const BATCH_SECTION_ID = "product-list-batch";
class ProductListLoader extends HTMLElement {
  /** Holds the cards; on desktop it is also what scrolls. */
  scroller = null;
  /** The theme scrolls `.product-list__inner` on desktop but its `.scroller` parent on mobile; watch both. */
  scrollContainers = [];
  productList = null;
  isLoading = false;
  isDone = false;
  connectedCallback() {
    this.productList = this.closest("product-list");
    this.scroller = this.productList?.querySelector(".product-list__inner") ?? null;
    if (!this.productList || !this.scroller) {
      return;
    }
    this.scrollContainers = [this.scroller, this.scroller.parentElement].filter((el) => el instanceof HTMLElement);
    this.scrollContainers.forEach((el) => el.addEventListener("scroll", this.onScroll, { passive: true }));
    this.productList.addEventListener("pointerenter", this.loadNext, { once: true });
    this.productList.addEventListener("touchstart", this.loadNext, { once: true, passive: true });
    this.productList.addEventListener("focusin", this.loadNext, { once: true });
  }
  disconnectedCallback() {
    this.scrollContainers.forEach((el) => el.removeEventListener("scroll", this.onScroll));
  }
  get nextPage() {
    return Number(this.dataset.nextPage);
  }
  get limit() {
    return Number(this.dataset.limit);
  }
  get cardCount() {
    return this.scroller?.querySelectorAll(":scope > product-item").length ?? 0;
  }
  onScroll = () => {
    const container = this.scrollContainers.find((el) => el.scrollWidth - el.clientWidth > 1);
    if (!container) {
      return;
    }
    const remaining = container.scrollWidth - container.clientWidth - Math.abs(container.scrollLeft);
    if (remaining < container.clientWidth * PREFETCH_VIEWPORTS) {
      this.loadNext();
    }
  };
  loadNext = async () => {
    if (this.isLoading || this.isDone || !this.scroller || !this.dataset.url) {
      return;
    }
    this.isLoading = true;
    try {
      const cards = await this.fetchBatch({ url: this.dataset.url, page: this.nextPage });
      const room = this.limit - this.cardCount;
      const toAppend = cards.slice(0, Math.max(room, 0));
      this.appendCards(toAppend);
      this.dataset.nextPage = String(this.nextPage + 1);
      if (toAppend.length < cards.length || cards.length === 0 || this.cardCount >= this.limit) {
        this.finish();
      }
    } catch (error) {
      logger.error("Loading more products failed", error);
      this.finish();
    } finally {
      this.isLoading = false;
    }
    this.onScroll();
  };
  fetchBatch = async ({ url, page }) => {
    const separator = url.includes("?") ? "&" : "?";
    const response = await fetch(`${url}${separator}section_id=${BATCH_SECTION_ID}&page=${page}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const html = (await response.text()).replaceAll(BATCH_SECTION_ID, `${BATCH_SECTION_ID}-${this.productList?.id ?? ""}`);
    const doc = new DOMParser().parseFromString(html, "text/html");
    return Array.from(doc.querySelectorAll("product-item"));
  };
  appendCards = (cards) => {
    if (!this.scroller || cards.length === 0) {
      return;
    }
    const sizes = this.scroller.querySelector("product-item img[sizes]")?.sizes;
    if (sizes) {
      cards.forEach((card) => card.querySelectorAll("img[sizes]").forEach((img) => img.sizes = sizes));
    }
    this.scroller.append(...cards.map((card) => document.adoptNode(card)));
    this.syncNextButton();
  };
  /** The theme's next button disables itself from the scroll width at click time, before new cards arrive. */
  syncNextButton = () => {
    const nextButton = this.productList?.querySelector(".prev-next-button--next");
    if (!nextButton || !this.scroller) {
      return;
    }
    const atEnd = Math.abs(this.scroller.scrollLeft) + this.scroller.clientWidth >= this.scroller.scrollWidth - 1;
    nextButton.toggleAttribute("disabled", atEnd);
  };
  finish = () => {
    this.isDone = true;
    this.scrollContainers.forEach((el) => el.removeEventListener("scroll", this.onScroll));
  };
}
customElements.define("variant-siblings", VariantSiblings);
customElements.define("judgeme-reviews", JudgeMeReviews);
customElements.define("cookie-banner-component", CookieBanner);
customElements.define("product-description", ProductDescription);
customElements.define("product-list-loader", ProductListLoader);
